/* eslint-disable @typescript-eslint/no-explicit-any */
import { Rule } from 'antd/lib/form'
import { authApi } from '../api/api'
import store from '../store'
import { setUserList } from '../store/slices/admin/admin'
import { updateUser } from '../store/slices/user/user'
import { IUser } from '../store/slices/user/user.types'
import { STORAGE_KEYS } from './consts'
import { ISaveUser, REQUEST_STATUS } from './types'
import { UpdateUser } from '../components/Admin/UsersTable/UsersTable.types'
import { CustomFormInstance, ValueOf } from '../types'
import {
    MachineForm,
    PrimaryAdditionalComponent,
    PrimaryComponent,
    SubComponent,
} from './forms/methods/types'

export interface IInviteUser {
    firstName: string
    lastName: string
    email: string
}

export const isLoading = (val: REQUEST_STATUS): boolean => {
    return val === REQUEST_STATUS.PENDING
}

export const getRegularRules = (val: string): Rule[] => {
    return [
        {
            required: true,
            message: `${val} is required`,
        },
    ]
}

export const fetchSaveUser = (data: ISaveUser, user: IUser): Promise<void> => {
    return authApi
        .put(`/api/users/update/${localStorage.getItem(STORAGE_KEYS.ID)}`, {
            ...data,
        })
        .then(() => {
            store.dispatch(
                updateUser({
                    ...user,
                    ...data,
                })
            )
        })
        .catch((err) => {
            return Promise.reject(err)
        })
}

export const getClaimNum = (num: number, offset = 0) => {
    const newNum = num + offset
    let prefix = ''
    if (newNum < 100) {
        prefix += '0'
    }
    if (newNum < 10) {
        prefix += '0'
    }
    return `${prefix}${newNum}`
}

export const fetchAllUsers = (): Promise<void> => {
    return authApi
        .get(`/api/users/`)
        .then(({ data }: any) => {
            store.dispatch(setUserList(data))
        })
        .catch((err) => {
            return Promise.reject(err)
        })
}

export const fetchSaveUserAccess = (
    id: number,
    disabled: boolean
): Promise<void> => {
    return authApi
        .put(`/api/users/access/update/${id}`, {
            disabled,
        })
        .then(() => {
            fetchAllUsers()
        })
        .catch((err) => {
            return Promise.reject(err)
        })
}

export const fetchSubscriptionConfig = (): Promise<any> => {
    return authApi
        .get(`/api/charge/pre-subscription`)
        .then(({ data }) => {
            return data
        })
        .catch((err) => {
            return Promise.reject(err)
        })
}

export const fetchSaveSubscriptionSuccess = (
    paymentIntentSecret: string,
    paymentIntentId: string,
    data: ISaveUser
): Promise<boolean> => {
    return authApi
        .put(`/api/charge/subscription/update`, {
            paymentIntentSecret,
            paymentIntentId,
        })
        .then(() => {
            store.dispatch(
                updateUser({
                    ...data,
                    subscribed: true,
                })
            )
            return true
        })
        .catch((err) => {
            return Promise.reject(err)
        })
}

export const fetchSaveSubscriptionCancel = (
    data: ISaveUser
): Promise<boolean> => {
    return authApi
        .put(`/api/charge/subscription/cancel`)
        .then(() => {
            store.dispatch(
                updateUser({
                    ...data,
                    subscribed: false,
                })
            )
            return true
        })
        .catch((err) => {
            return Promise.reject(err)
        })
}

export const fetchInviteUser = (data: IInviteUser): Promise<void> => {
    return authApi
        .post('/api/users/invite', data)
        .then(() => fetchAllUsers())
        .catch((err) => {
            return Promise.reject(err)
        })
}
export const setPassword = (data: {
    token: string
    password: string
}): Promise<void> => {
    return authApi
        .post('/api/auth/set-password', data)
        .then(() => undefined)
        .catch((err) => Promise.reject(err))
}

export const AdminDeleteUser = async (id: number): Promise<void> => {
    await authApi.delete(`/api/users/admin/delete/${id}`)
}

export const AdminUpdateUser = async (
    id: number,
    data: UpdateUser
): Promise<boolean> => {
    const res = await authApi.put(`/api/users/admin/update/${id}`, data)
    return res.data
}

export const generateId = () => Math.random().toString(36).substr(2, 9)

export const extractIndefiniteArticle = (
    item: string,
    form: CustomFormInstance | CustomFormInstance<MachineForm>
): string => {
    const typedFormRef = form as CustomFormInstance<MachineForm>
    let article = 'a' // safe fallback
    if (item) {
        if (item.includes('+=>')) {
            const splitItem = item.split('+=>')
            const subComponents =
                typedFormRef.getFieldValue<
                    ValueOf<MachineForm['primaryComponentSubComponentsList']>
                >(['primaryComponentSubComponentsList', splitItem[0]]) ?? []
            const subComp = subComponents.find(
                (comp: SubComponent) => comp?.id === splitItem[1]
            )
            if (subComp?.indefiniteArticle !== undefined) {
                article =
                    subComp.indefiniteArticle === ' '
                        ? ''
                        : subComp.indefiniteArticle
            }
        } else if (item.includes('add_')) {
            const additionalComponents = typedFormRef.getFieldValue<
                MachineForm['primaryAdditionalComponents']
            >(['primaryAdditionalComponents'])
            const addComp = additionalComponents?.find(
                (x: PrimaryAdditionalComponent) => x?.id === item.split('_')[1]
            )
            if (addComp?.indefiniteArticle !== undefined) {
                article =
                    addComp.indefiniteArticle === ' '
                        ? ''
                        : addComp.indefiniteArticle
            }
        } else {
            const primaryComponents = typedFormRef.getFieldValue<
                MachineForm['primaryComponents']
            >(['primaryComponents'])
            const comp = primaryComponents?.find(
                (x: PrimaryComponent) => x?.id === item
            )
            if (comp?.indefiniteArticle !== undefined) {
                article =
                    comp.indefiniteArticle === ' ' ? '' : comp.indefiniteArticle
            }
        }
    }
    return article
}

export const extractComponentName = (
    item: string,
    form: CustomFormInstance | CustomFormInstance<MachineForm>
) => {
    const typedFormRef = form as CustomFormInstance<MachineForm>
    let finalText = item
    if (item) {
        if (item.includes('+=>')) {
            const splitItem = item.split('+=>')
            const primaryComponents = typedFormRef.getFieldValue<
                MachineForm['primaryComponents']
            >(['primaryComponents'])
            const primaryComponentName =
                primaryComponents.find(
                    (x: PrimaryComponent) => x?.id === splitItem[0]
                )?.component ?? ''
            const subComponents =
                typedFormRef.getFieldValue<
                    ValueOf<MachineForm['primaryComponentSubComponentsList']>
                >(['primaryComponentSubComponentsList', splitItem[0]]) ?? []
            const subComponentName =
                subComponents.find(
                    (comp: SubComponent) => comp?.id === splitItem[1]
                )?.component ?? ''
            finalText = `${subComponentName} of the ${primaryComponentName}`
        } else if (item.includes('add_')) {
            const additionalComponents = typedFormRef.getFieldValue<
                MachineForm['primaryAdditionalComponents']
            >(['primaryAdditionalComponents'])
            const additionalComponentName =
                additionalComponents.find(
                    (x: PrimaryAdditionalComponent) =>
                        x?.id === item.split('_')[1]
                )?.component ?? ''
            finalText = additionalComponentName
        } else {
            const primaryComponents = typedFormRef.getFieldValue<
                MachineForm['primaryComponents']
            >(['primaryComponents'])
            const primaryComponentName =
                primaryComponents.find((x: PrimaryComponent) => x?.id === item)
                    ?.component ?? ''
            finalText = primaryComponentName
        }
    }

    return finalText
}
